import { useState } from "react";
import { Link } from "react-router-dom";
import Icon from "../components/Icon";

const CATEGORIES = ["All Posts", "Tajweed", "Hifz", "For Parents"];

const ARTICLES = [
  {
    id: 1,
    category: "Getting Started",
    filter: "All Posts",
    title: "How to Start Your Quran Journey",
    excerpt:
      "Embarking on a journey to learn the Quran can feel overwhelming. Discover the five essential steps to begin your path with clarity and devotion.",
    content: [
      "Embarking on a journey to learn the Quran is one of the most rewarding endeavors a Muslim can undertake. However, for beginners, kids, or adults returning to their studies, it can sometimes feel a bit overwhelming. The key is to start with clear steps, realistic goals, and a pure intention.",
      "The first step is establishing your Intention (Niyyah). Before looking at any books or apps, ask yourself why you want to learn. Your goal should be to seek the pleasure of Allah, build a deeper relationship with His words, and apply the teachings in your daily life. A pure intention will provide you with consistency and patience.",
      "Second, find the right starting point. If you cannot read the Arabic script yet, you must start with Noorani Qaida. Noorani Qaida is the gold standard for learning pronunciation, letter joining, and phonics. Skipping this foundational step often leads to persistent mistakes in reciting later on.",
      "Third, create a daily routine. Consistency is far more valuable than intensity. Spending 15 to 20 minutes every day learning and practicing is much better than studying for two hours once a week. Set a specific, quiet time of day, such as after Fajr or Maghrib, to dedicate to your Quran studies.",
      "Fourth, learn from a qualified tutor. While self-study apps are helpful tools, they cannot correct your pronunciation or tell you if you are pronouncing letters from the correct throat points (Makharij). An experienced teacher provides direct feedback, encouragement, and holds you accountable.",
      "Lastly, be patient and persistent. Remember the famous Hadith of the Prophet Muhammad (peace be upon him): 'The one who recites the Quran and stammers in it, and has difficulty, will have a double reward.' Every struggle is rewarded, so keep going and never lose hope."
    ],
    readTime: "5 min read",
    image:
      "https://lh3.googleusercontent.com/aida-public/AB6AXuDvgTtynn3TFYA71yNEoUEhkFp2MMLVoeMvUHQSBxFsdMv55iaqwdyAie0VRisR7OCB2E4uOXBFeBYApH0BNsKoVdiftepkl3xfE87XZQyvO-OypzB5T9h6hBkIBz-Lejl5qw9NO0HechCVvTw2MC4GOS10_WehLF-n-AtX8kJRjDPV2eVrbzREMgDf4JacISYGRgy0JbCJWWhLV-o_POPgud7Y80F5PImDZOw0IrvxCk2CaEbKu3Fx8CGpno3cmDw26EWp0FIger1K",
    alt: "Quran study desk",
    date: "August 10, 2026",
    author: "Ustadha Aisha",
  },
  {
    id: 2,
    category: "Tajweed",
    filter: "Tajweed",
    title: "Why Tajweed Matters for Every Reciter",
    excerpt:
      "Proper pronunciation isn't just about melody; it's about preserving the meaning of Allah's words. Learn the spiritual benefits of Tajweed.",
    content: [
      "Proper pronunciation isn't just about melody; it's about preserving the meaning of Allah's words. The Arabic language is incredibly rich and precise. A slight change in the pronunciation of a letter or the duration of a vowel can completely alter the meaning of a word, sometimes leading to major theological errors.",
      "Tajweed refers to the rules governing the pronunciation and recitation of the Holy Quran. It teaches us how to articulate every letter from its proper origin (Makhraj) and give it its correct characteristics (Sifaat). It also includes rules for elongation, pausing, and merging letters.",
      "Reciting with Tajweed is not just an optional decoration; it is a spiritual obligation for those who wish to recite the Quran correctly. By honoring the rules of Tajweed, we recite the Quran exactly as it was revealed to the Prophet Muhammad (peace be upon him) by the Angel Jibreel.",
      "Additionally, Tajweed enhances the emotional and spiritual impact of the recitation. When you recite slowly and with correct pronunciation, you naturally engage in reflection (Tadabbur). The beauty of the speech of Allah becomes manifest, softening the heart and drawing you closer to your Creator."
    ],
    readTime: "8 min read",
    image:
      "https://lh3.googleusercontent.com/aida-public/AB6AXuBKI0-6lrWTzTqEDUtWaLGRqtYXcOyz7AlVXgVLAV47p8XtGQ3DmJjIvhNUKWO5VMOPkNRTdgl2a7S5oeI2l7SrmGRzUpSa81h-ILZjf4tgy4ChvNRYkxbZ9OnB58pQPnNv8PoqPu0EHVbe0gNu_iqGslhPOc1rJAwMOpXhMRYWPUrR5TM00sIe5jo-83CQhyECVzccWYzJ9NcVErtHNGA2lj1YrxC82ADkMYRM_fYm2yAK0Fn1PTBGsEBwrQrNQTfbz6xpfGS_xwDD",
    alt: "Islamic architecture detail",
    date: "August 8, 2026",
    author: "Sheikh Yusuf",
  },
  {
    id: 3,
    category: "Hifz",
    filter: "Hifz",
    title: "Practical Tips for Effective Hifz",
    excerpt:
      "Memorization is a marathon, not a sprint. We share proven techniques to help you retain verses longer and with more precision.",
    content: [
      "Memorizing the Holy Quran (Hifz) is a monumental task that requires dedication, planning, and a systematic approach. Many start their Hifz journey with great enthusiasm only to lose momentum after a few weeks. To succeed, one must view memorization as a long-term marathon rather than a sprint.",
      "The secret to successful Hifz is prioritizing revision (Mura'jaah) over new memorization. Your existing memorization is like a building, and revision is the foundation. If you keep adding new floors without reinforcing the foundation, the structure will collapse. A good rule of thumb is to spend 70% of your time revising what you already know and 30% memorizing new verses.",
      "Another practical tip is to memorize using the same physical copy (Mushaf) of the Quran. The brain relies heavily on visual memory. The location of a verse on the page, the style of the script, and even the margins help anchor the words in your mind. Switching between different editions or devices can cause visual confusion.",
      "Additionally, recite your memorized portions during your daily prayers (Salah). This acts as a practical reinforcement mechanism. If you can recite a passage smoothly during prayer, it is a strong sign that it has been successfully committed to your long-term memory."
    ],
    readTime: "12 min read",
    image:
      "https://lh3.googleusercontent.com/aida-public/AB6AXuD5ChffhHku_El9dU7VQeGKemgbTvGWhmy9xJBmyK85G78DUXxhWyeGZUNRW0DpwPuSxhnQ5uZReSb_vPTysySlFsrphyMqSvog_kUtWAGt-_muMTt680JcGcrW7NoyYNIWm9aijPkHvBfTENSq84LsuO3fXVQZMsfW73b3AWKojAoRvFKvqe4WeWwPYzSr7GNo4IyXRnuqW03AEpnJcxIJfxkQq68mh5H-x9gsv-KBvDu2tZXX7uVoF6ULrWhVFuAB8QNhdellX1S2",
    alt: "Night sky stars",
    date: "August 5, 2026",
    author: "Hafiz Muhammad",
  },
  {
    id: 4,
    category: "Learning",
    filter: "All Posts",
    title: "Benefits of 1-on-1 Personalized Classes",
    excerpt:
      "Why personalized attention makes all the difference in Quranic education. Explore the advantages of custom-paced learning.",
    content: [
      "Traditional group classrooms have their merits, but when it comes to learning the Quran, personalized 1-on-1 attention is unmatched. The rules of Tajweed, the corrections of memorization, and the pacing of lessons are highly unique to each individual student. In a group setting, a student might feel left behind or held back by others.",
      "In a 1-on-1 environment, the teacher focuses 100% of their attention on you. Every single sound you make is listened to and corrected in real-time. If you find a particular rule or sound difficult, your tutor can dedicate the entire lesson to helping you master it, without the pressure of other students waiting.",
      "Personalized classes also offer incredible scheduling flexibility. With the demands of modern school, work, and family life, finding time to travel to an Islamic center can be a barrier. Online 1-on-1 sessions allow you to choose times that fit your family schedule, making Quran learning sustainable.",
      "Lastly, a close teacher-student relationship is built. Tutors become mentors, guiding students through their personal spiritual milestones, providing emotional support, and tailoring teaching strategies to match the student's unique learning style."
    ],
    readTime: "6 min read",
    image:
      "https://lh3.googleusercontent.com/aida-public/AB6AXuC3MnZBdNu7L-oT5DGeNmIBlP_-i1F2NJUcgUkoexzxuDu_Kzwuk8OYJNogwI9nNu7FR3Naf2bcXqO-j1DhF_YANSBh4ne5S5lw9Q-Zf7rz8X8pDS4Te_AJ3Cn4tISx_G4zF-9Ln8yPqzvdg0truj4I9EawKpaB5HscpsOE-e0PhDoCa9iBlUM0cm013bmvGe7rEeG_f0W3tbr8JexKaJ1tB0n26V9II0SK6pn8GHzax1OheHFei9XkYwlFYMKKqas_9GBMyIwOEG7G",
    alt: "Online learning",
    date: "July 28, 2026",
    author: "Ustadha Fatima",
  },
  {
    id: 5,
    category: "Parents",
    filter: "For Parents",
    title: "Supporting Your Child's Quranic Progress",
    excerpt:
      "A guide for parents to create a nurturing environment at home for their children to thrive in their Quranic studies.",
    content: [
      "Enrolling your child in a Quran academy is a great first step, but the real progress happens at home. Parents play a crucial role in creating a nurturing environment that encourages regular study and instills a deep, lasting love for the Holy Quran.",
      "First, establish a dedicated space for Quran learning. It should be quiet, clean, free from distractions like televisions or phones, and well-lit. Having a specific 'Quran corner' makes the study time feel special and respected.",
      "Second, show genuine interest in their lessons. Ask them what they learned today, listen to them recite, and praise their efforts rather than just their test scores. Let them hear you reciting the Quran as well—children are far more likely to value the Quran if they see their parents valuing it in their own daily lives.",
      "Third, avoid making Quran study feel like a punishment. If a child is tired or frustrated, do not force them into a long session. Instead, break it down into shorter, positive intervals and celebrate small milestones with rewards. The goal is to build a positive emotional association with the Book of Allah."
    ],
    readTime: "10 min read",
    image:
      "https://lh3.googleusercontent.com/aida-public/AB6AXuAi6-AOED9DvJuUguzNxu86yjZ_ZF2Sx-bb0De28Km3J86me6TaJvjGTeSa99UyCGRbWbcVGBISj2jWZbCflfSGFyM6A6KRgVvqhu0xgNDH-TIcJLPPGl7gzYRx7J7zm-yLQqoECoDLfz4Jr6C5CG6UFtvtztsgiBXXeBtwbva24GIE-kVozgvn2rzmSS79H9K5FebEG7YbcnRGi2IBlKgXem-60S9CxqPAVB9orY4fxMnO1gAyJPxpShRDmhP_mkVewgNq-bikPsX3",
    alt: "Family reading",
    date: "July 24, 2026",
    author: "Sister Sarah",
  },
  {
    id: 6,
    category: "Basics",
    filter: "All Posts",
    title: "The Beginner's Guide to Arabic Letters",
    excerpt:
      "Starting from scratch? Learn how to master the Arabic alphabet and its pronunciation to build a solid foundation.",
    content: [
      "Starting your Arabic and Quranic journey from absolute scratch is a beautiful step. The key to mastering the Arabic alphabet is to focus on three distinct components: letter shapes, pronunciation points (Makharij), and short vowel markings (Harakaat).",
      "Arabic letters change their shape depending on their position in a word—whether they are at the beginning, middle, or end. For a beginner, practicing these different forms is like learning to read script. Using flashcards and writing the letters by hand is a proven way to train your eyes to recognize these shapes instantly.",
      "Pronunciation is another vital pillar. Arabic contains sounds that do not exist in English or other European languages. For example, distinguishing between the soft 'h' and the deep throat 'h' (Ha) takes conscious practice. Listen carefully to your tutor and practice the vocal exercises daily.",
      "Take it one step at a time. Do not try to learn all 28 letters in one day. Focus on a group of 3-4 letters, learn their sounds and written forms, and once you are confident, move on to the next set. Before you know it, you will be joining letters and reading full words."
    ],
    readTime: "15 min read",
    image:
      "https://lh3.googleusercontent.com/aida-public/AB6AXuB8RXnDSCiqVABjeT2PjJ3DpTOmNIc7tZU_JxfbyQ1hFZ1cye1Wdih-o5s3wk6vZaiA75EBtaRSpu0lJJQGZpCW7pJYLrbnozfSVBBmxoo8lLg1o8S6KR4T-FtN-IpcIth3YnYug3gCC3FpicfhsuzjCSuVpmbyXI1hSenzSeO9EUUeuMJQ-Gtq0RNgWlGaBwbs21FdGbSONZ5YQWNLJTxX7_ekdBxzmiDCgX5xSb6u0O5exuGy49kHjMkIE4MTNb6iPKRb-TiMGnNB",
    alt: "Pristine mosque courtyard",
    date: "July 15, 2026",
    author: "Ustadha Aisha",
  },
];

export default function BlogPage() {
  const [activeFilter, setActiveFilter] = useState("All Posts");
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const filtered =
    activeFilter === "All Posts"
      ? ARTICLES
      : ARTICLES.filter((a) => a.filter === activeFilter);

  const ITEMS_PER_PAGE = 3;
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);

  // Sliced articles for active page
  const displayedArticles = filtered.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    setCurrentPage(1);
  };

  const handleArticleClick = (article) => {
    setSelectedArticle(article);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBackToArticles = () => {
    setSelectedArticle(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (selectedArticle) {
    return (
      <>
        <section className="py-12 md:py-20 bg-surface-container-low min-h-[60vh] page-x">
          <div className="max-w-3xl mx-auto bg-white rounded-3xl p-6 md:p-12 shadow-xl border border-outline-variant relative">
            <button
              onClick={handleBackToArticles}
              className="inline-flex items-center gap-2 text-primary font-bold hover:gap-3 transition-all mb-8 font-label-md text-label-md group"
            >
              <Icon name="arrow_back" className="material-symbols-outlined transition-transform group-hover:-translate-x-1" />
              Back to Articles
            </button>

            <div className="flex flex-wrap items-center gap-4 mb-6">
              <span className="bg-primary-fixed text-on-primary-fixed px-3 py-1 rounded-lg font-label-md text-label-md shadow-sm">
                {selectedArticle.category}
              </span>
              <span className="text-on-surface-variant font-caption text-caption flex items-center">
                <Icon name="schedule" className="material-symbols-outlined text-[16px] mr-1" />
                {selectedArticle.readTime}
              </span>
              <span className="text-on-surface-variant font-caption text-caption">
                {selectedArticle.date}
              </span>
            </div>

            <h1 className="text-display-responsive text-primary mb-8 leading-tight">
              {selectedArticle.title}
            </h1>

            <div className="w-full h-64 md:h-[400px] rounded-2xl overflow-hidden shadow-md mb-10">
              <img
                src={selectedArticle.image}
                alt={selectedArticle.alt}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="space-y-6 text-on-surface-variant font-body-lg text-body-lg leading-relaxed text-justify">
              {selectedArticle.content.map((p, index) => {
                if (index === 0) {
                  return (
                    <p key={index} className="first-letter:text-5xl first-letter:font-bold first-letter:text-primary first-letter:mr-3 first-letter:float-left first-letter:h-12">
                      {p}
                    </p>
                  );
                }
                return <p key={index}>{p}</p>;
              })}
            </div>

            <div className="mt-12 p-8 rounded-3xl bg-primary text-on-primary relative overflow-hidden celestial-glow">
              <div className="absolute -bottom-10 -right-10 opacity-10">
                <Icon name="auto_stories" className="material-symbols-outlined text-[150px]" />
              </div>
              <div className="relative z-10">
                <h3 className="font-headline-md text-headline-md text-tertiary-fixed mb-2 font-bold">
                  Start Your Journey Today
                </h3>
                <p className="font-body-md text-body-md mb-6 opacity-90">
                  Ready to apply what you've learned? Book a free trial class with our experienced Quran tutors.
                </p>
                <Link
                  to="/contact"
                  className="inline-block bg-tertiary-container text-on-tertiary-container px-6 py-3 rounded-full font-bold hover:scale-105 active:scale-95 transition-all text-center"
                >
                  Book Free Trial
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-primary-container text-on-primary-container py-16 md:py-20 page-x">
          <div className="max-w-max-width mx-auto flex flex-col md:flex-row items-center justify-between gap-12">
            <div className="max-w-xl">
              <h2 className="text-headline-responsive mb-4 text-primary-fixed">
                Subscribe to our newsletter
              </h2>
              <p className="font-body-lg text-body-lg opacity-90">
                Receive weekly tips on Tajweed, Hifz techniques, and exclusive
                updates from the Online Quran Universe academy directly in your
                inbox.
              </p>
            </div>
            <div className="w-full md:w-auto flex flex-col sm:flex-row gap-4">
              <input
                className="px-6 py-4 rounded-full bg-surface-container-low text-on-surface border-none focus:ring-2 focus:ring-tertiary-container w-full sm:w-80 outline-none"
                placeholder="Your email address"
                type="email"
                aria-label="Newsletter email"
              />
              <button
                type="button"
                className="px-6 sm:px-8 py-3 sm:py-4 rounded-full w-full sm:w-auto text-center bg-tertiary-container text-on-tertiary-container font-bold hover:scale-105 transition-transform active:scale-95"
              >
                Join Now
              </button>
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <section className="relative section-y celestial-gradient-linear overflow-hidden">
        <div className="max-w-max-width mx-auto page-x relative z-10 text-center">
          <span className="inline-block px-4 py-1 rounded-full bg-primary-fixed text-on-primary-fixed font-label-md text-label-md mb-6 uppercase tracking-wider">
            Education &amp; Insight
          </span>
          <h1 className="text-display-responsive text-primary mb-6">
            Quran Learning Resources
          </h1>
          <p className="font-body-lg text-body-lg text-on-surface-variant max-w-2xl mx-auto">
            Explore our collection of guides, tips, and insights designed to
            support your spiritual journey and mastery of the Holy Quran.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-20 max-w-max-width mx-auto page-x">
        <div className="flex flex-col md:flex-row justify-between items-center md:items-end mb-12 gap-6">
          <div className="flex flex-col items-center md:items-start text-center md:text-left">
            <h2 className="text-headline-responsive text-primary mb-2">
              Latest Articles
            </h2>
            <div className="h-1 w-20 bg-tertiary-container rounded-full" />
          </div>
          <div className="flex flex-wrap justify-center md:justify-start gap-2.5 sm:gap-3 md:gap-4 w-full md:w-auto">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => handleFilterChange(cat)}
                className={`px-5 py-2 rounded-full whitespace-nowrap font-label-md text-label-md transition-colors ${activeFilter === cat
                  ? "bg-primary text-on-primary"
                  : "bg-surface-container-high text-on-surface-variant hover:bg-outline-variant"
                  }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {displayedArticles.length === 0 ? (
          <p className="text-center text-on-surface-variant font-body-md py-12">
            No articles in this category yet. Check back soon!
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-gutter">
            {displayedArticles.map((article) => (
              <article
                key={article.id}
                onClick={() => handleArticleClick(article)}
                className="bg-surface-container-lowest rounded-2xl border border-outline-variant hover:shadow-lg hover:-translate-y-1 cursor-pointer transition-all duration-300 flex flex-col group overflow-hidden"
              >
                <div className="h-56 overflow-hidden relative">
                  <img
                    alt={article.alt}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    src={article.image}
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-primary-fixed text-on-primary-fixed px-3 py-1 rounded-lg font-label-md text-label-md shadow-sm">
                      {article.category}
                    </span>
                  </div>
                </div>
                <div className="p-8 flex flex-col flex-grow">
                  <h3 className="font-headline-md text-headline-md text-primary mb-3 leading-tight group-hover:text-tertiary transition-colors">
                    {article.title}
                  </h3>
                  <p className="font-body-md text-body-md text-on-surface-variant mb-6 line-clamp-3">
                    {article.excerpt}
                  </p>
                  <div className="mt-auto pt-4 border-t border-surface-variant flex justify-between items-center">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleArticleClick(article);
                      }}
                      className="text-primary font-bold flex items-center hover:gap-2 transition-all font-label-md text-label-md"
                    >
                      Read Article{" "}
                      <Icon
                        name="arrow_forward"
                        className="material-symbols-outlined ml-1"
                      />
                    </button>
                    <span className="text-on-surface-variant font-caption text-caption flex items-center">
                      <Icon
                        name="schedule"
                        className="material-symbols-outlined text-[16px] mr-1"
                      />{" "}
                      {article.readTime}
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="mt-16 flex justify-center items-center gap-2">
            <button
              type="button"
              onClick={() => {
                if (currentPage > 1) {
                  setCurrentPage((prev) => prev - 1);
                  window.scrollTo({ top: 400, behavior: "smooth" });
                }
              }}
              disabled={currentPage === 1}
              className={`w-10 h-10 rounded-full border border-outline-variant flex items-center justify-center text-on-surface-variant hover:bg-surface-container hover:text-primary transition-all ${currentPage === 1 ? "opacity-40 cursor-not-allowed" : ""
                }`}
              aria-label="Previous page"
            >
              <Icon name="chevron_left" className="material-symbols-outlined" />
            </button>

            {Array.from({ length: totalPages }, (_, index) => {
              const pageNumber = index + 1;
              const isActive = pageNumber === currentPage;
              return (
                <button
                  key={pageNumber}
                  type="button"
                  onClick={() => {
                    setCurrentPage(pageNumber);
                    window.scrollTo({ top: 400, behavior: "smooth" });
                  }}
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${isActive
                    ? "bg-primary text-on-primary"
                    : "border border-outline-variant text-on-surface-variant hover:bg-surface-container hover:text-primary"
                    }`}
                >
                  {pageNumber}
                </button>
              );
            })}

            <button
              type="button"
              onClick={() => {
                if (currentPage < totalPages) {
                  setCurrentPage((prev) => prev + 1);
                  window.scrollTo({ top: 400, behavior: "smooth" });
                }
              }}
              disabled={currentPage === totalPages}
              className={`w-10 h-10 rounded-full border border-outline-variant flex items-center justify-center text-on-surface-variant hover:bg-surface-container hover:text-primary transition-all ${currentPage === totalPages ? "opacity-40 cursor-not-allowed" : ""
                }`}
              aria-label="Next page"
            >
              <Icon name="chevron_right" className="material-symbols-outlined" />
            </button>
          </div>
        )}
      </section>

      <section className="bg-primary-container text-on-primary-container py-16 md:py-20 page-x">
        <div className="max-w-max-width mx-auto flex flex-col md:flex-row items-center justify-between gap-12">
          <div className="max-w-xl">
            <h2 className="text-headline-responsive mb-4 text-primary-fixed">
              Subscribe to our newsletter
            </h2>
            <p className="font-body-lg text-body-lg opacity-90">
              Receive weekly tips on Tajweed, Hifz techniques, and exclusive
              updates from the Online Quran Universe academy directly in your
              inbox.
            </p>
          </div>
          <div className="w-full md:w-auto flex flex-col sm:flex-row gap-4">
            <input
              className="px-6 py-4 rounded-full bg-surface-container-low text-on-surface border-none focus:ring-2 focus:ring-tertiary-container w-full sm:w-80 outline-none"
              placeholder="Your email address"
              type="email"
              aria-label="Newsletter email"
            />
            <button
              type="button"
              className="px-6 sm:px-8 py-3 sm:py-4 rounded-full w-full sm:w-auto text-center bg-tertiary-container text-on-tertiary-container font-bold hover:scale-105 transition-transform active:scale-95"
            >
              Join Now
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
